import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Search } from 'react-feather';

export interface SearchBarProps {
  onSearch: (value: string) => void;
  initialValue?: string;
}

interface ExtendedSearchBarProps extends SearchBarProps {
  shouldReset?: boolean;
  onResetComplete?: () => void;
  initialValue?: string;
}

const SearchBar: React.FC<ExtendedSearchBarProps> = ({
  onSearch,
  shouldReset,
  onResetComplete,
  initialValue = '',
}) => {
  const [inputValue, setInputValue] = useState(initialValue);

  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      onSearch(term);
    }, 500),
    [onSearch],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setInputValue(value);

    if (value === '') {
      onSearch('');
      debouncedSearch.cancel();
      return;
    }

    debouncedSearch(value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(inputValue);
    debouncedSearch.cancel();
  };

  useEffect(() => {
    if (shouldReset) {
      setInputValue('');
      onSearch('');
      debouncedSearch.cancel();
      onResetComplete?.();
    }
  }, [shouldReset, onSearch, debouncedSearch, onResetComplete]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div className="mb-0 flex justify-end rounded-tl-sm rounded-tr-sm border border-[#DEDEDE] bg-[#F1F1F1]">
      <form onSubmit={handleSubmit} className="flex w-full">
        <div className="flex w-full items-center gap-2">
          <div className="relative flex-1">
            <label htmlFor="search" className="sr-only">
              Search Something
            </label>
            <input
              id="search"
              type="text"
              placeholder="Search"
              className="w-full rounded-tl-sm rounded-tr-sm px-8 py-2 pr-10 outline-none"
              value={inputValue}
              onChange={handleChange}
            />
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400"
              aria-hidden="true"
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default SearchBar;
